import { api } from 'services/api';

import { OrganizationUser } from 'types/access-control/organization';
import {
  Opportunity,
  OpportunityFile,
  OpportunityFilters,
  OpportunityFlowStatusAction,
  OpportunityFlowSummary,
  OpportunityHistoryEvent,
  OpportunityHistoryPage,
  OpportunityOrigin,
  OpportunityPayload,
} from 'types/opportunity';

type FetchOpportunityFlowSummaryParams = {
  organizationId: number;
  filters?: OpportunityFilters;
  includeOpportunities?: boolean;
  sort?: string;
};

export const buildOpportunityFilterParams = (
  filters: OpportunityFilters = {},
): Record<string, string | number> =>
  Object.entries(filters).reduce<Record<string, string | number>>(
    (params, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return params;
      }

      return { ...params, [`filter[${key}]`]: value };
    },
    {},
  );

export const fetchOpportunityFlowSummary = async ({
  organizationId,
  filters,
  includeOpportunities = false,
  sort,
}: FetchOpportunityFlowSummaryParams): Promise<OpportunityFlowSummary[]> => {
  const { data } = await api.get('/opportunity/dashboard/flow', {
    params: {
      'filter[organization_id]': organizationId,
      ...(includeOpportunities ? { 'filter[include_opportunities]': 1 } : {}),
      ...buildOpportunityFilterParams(filters),
      ...(sort ? { sort } : {}),
    },
  });

  return Object.values(data);
};

export const fetchOpportunity = async (id: number): Promise<Opportunity> => {
  const { data } = await api.get(`/opportunity/${id}`);

  return data.data;
};

export const fetchOpportunityOrigins = async (): Promise<
  OpportunityOrigin[]
> => {
  const { data } = await api.get('/opportunity/origin');

  return data.data;
};

export const fetchOrganizationUsers = async (
  organizationId: number,
): Promise<OrganizationUser[]> => {
  const { data } = await api.get('/organization/user', {
    params: {
      'filter[organization_id]': organizationId,
      'filter[status]': 'active',
      per_page: 9999,
    },
  });

  return data.data || [];
};

export const createOpportunity = async (
  payload: OpportunityPayload,
): Promise<Opportunity> => {
  const { data } = await api.post('/opportunity', payload);

  return data.data;
};

export const updateOpportunity = async (
  id: number,
  payload: OpportunityPayload,
): Promise<Opportunity> => {
  const { data } = await api.put(`/opportunity/${id}`, payload);

  return data.data;
};

export const deleteOpportunity = async (id: number): Promise<void> => {
  await api.delete(`/opportunity/${id}`);
};

export const fetchOpportunityFiles = async (
  opportunityId: number,
): Promise<OpportunityFile[]> => {
  const { data } = await api.get(`/opportunity/${opportunityId}/files`);

  return data.data || [];
};

export const uploadOpportunityFile = async ({
  opportunityId,
  file,
}: {
  opportunityId: number;
  file: File;
}): Promise<OpportunityFile> => {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await api.post(
    `/opportunity/${opportunityId}/files`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );

  return data.data;
};

export const deleteOpportunityFile = async ({
  opportunityId,
  fileId,
}: {
  opportunityId: number;
  fileId: number;
}): Promise<void> => {
  await api.delete(`/opportunity/${opportunityId}/files/${fileId}`);
};

export const fetchOpportunityHistory = async ({
  opportunityId,
  page = 1,
  perPage = 20,
}: {
  opportunityId: number;
  page?: number;
  perPage?: number;
}): Promise<OpportunityHistoryPage> => {
  const { data } = await api.get('/flow/history', {
    params: {
      'filter[object_type]': 'opportunity',
      'filter[object_id]': opportunityId,
      page,
      per_page: perPage,
      sort: '-created_at',
    },
  });

  return { data: data.data || [], meta: data.meta };
};

export const createOpportunityHistoryNote = async ({
  opportunityId,
  message,
}: {
  opportunityId: number;
  message: string;
}): Promise<OpportunityHistoryEvent> => {
  const { data } = await api.post('/flow/history', {
    object_type: 'opportunity',
    object_id: opportunityId,
    type: 'manual',
    message,
  });

  return data.data;
};

// Equivalente a `PUT budget/flow/status`; usado para tirar oportunidades de "Sem status".
export const setOpportunityFlowStatus = async ({
  opportunityId,
  flowStatusId,
}: {
  opportunityId: number;
  flowStatusId: number;
}): Promise<void> => {
  await api.put('/opportunity/flow/status', {
    opportunities: [
      { opportunity_id: opportunityId, flow_status_id: flowStatusId },
    ],
  });
};

type FlowStatusActionTarget = { id: number; key: string | null };

type FlowStatusActionResponse = {
  id: number;
  name: string;
  key: string | null;
  // O backend serializa a relação `belongsTo` com `->get()`, então chega como lista.
  target_status: FlowStatusActionTarget[] | FlowStatusActionTarget | null;
};

// Ações disponíveis a partir da etapa atual, filtradas pelo backend conforme os papéis do usuário.
export const fetchOpportunityFlowStatusActions = async (
  flowStatusId: number,
): Promise<OpportunityFlowStatusAction[]> => {
  const { data } = await api.get<{ data: FlowStatusActionResponse[] }>(
    `/flow/status/${flowStatusId}/action`,
  );

  return data.data.map((action) => {
    const target = Array.isArray(action.target_status)
      ? action.target_status[0]
      : action.target_status;

    return {
      id: action.id,
      name: action.name,
      key: action.key ?? null,
      target_status_id: target?.id ?? null,
      target_status_key: target?.key ?? null,
    };
  });
};

export const chooseOpportunityFlowAction = async ({
  actionId,
  opportunityId,
  message,
}: {
  actionId: number;
  opportunityId: number;
  message: string;
}): Promise<void> => {
  await api.post(`/flow/action/${actionId}`, {
    object_type: 'opportunity',
    object_id: opportunityId,
    type: 'manual',
    message,
  });
};
